import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import bytes from 'bytes';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useRouter } from 'next/router';

import { NonEmptyStringSchema } from '../../src/shared/types';
import { file2Base64 } from '../../src/form-utils';

import type { NonEmptyString } from 'src/shared/types';
import type { ChangeEvent } from 'react';

export const ImageUploader = ({
    image,
    setImage,
    title,
}: {
    setImage: (image: { name: NonEmptyString; data: NonEmptyString; binary: Blob } | null) => void;
    image: null | { name?: NonEmptyString; data?: NonEmptyString; url?: NonEmptyString };
    title?: string;
}) => {
    const { t } = useTranslation('restaurantAdmin.element.form');
    const MAX_IMAGE_SIZE = '10MB';
    const { enqueueSnackbar } = useSnackbar();
    const id = useMemo(() => uuidv4(), []);
    const router = useRouter();
    const upload = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
        const image = e.target.files?.[0] || null;
        if (image === null) return;
        if (image.size > bytes.parse(MAX_IMAGE_SIZE)) {
            enqueueSnackbar(t('error.imageMaxSizeError', { maxImageSize: MAX_IMAGE_SIZE }), { variant: 'error' });

            return;
        }
        const data = (await file2Base64(image)).split(',', 2);
        setImage({ binary: image, data: NonEmptyStringSchema.parse(data[1]), name: NonEmptyStringSchema.parse(image.name) });
    }, [enqueueSnackbar, t, setImage]);

    return (
        <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
            <label htmlFor={id}>
                <input accept="image/png,image/jpeg,.png,.jpg,.jpeg" id={id} onChange={upload} onClick={(event) => {
                    //@ts-expect-error
                    event.target.value = '';
                }} style={{ display: 'none' }} type="file"
                />
                <img
                    alt="Upload"
                    src={image === null ? `${router.basePath}/img/empty-image.jpg`
                        : image.data !== undefined ? `data:image/png;base64,${image.data}`
                            : image.url}
                    style={{ boxShadow: '0px 0px 4px 0px #0000007a', maxHeight: '200px', maxWidth: '200px' }}
                    title={title}
                />
            </label>
            {image !== null && <Button onClick={() => setImage(null)} variant="text">{t('button.label.deleteImage')}</Button>}
        </Box>
    );
};

